import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { store } from "Store";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Row, Col, Card, Form } from "react-bootstrap";
import { TextButton } from "Components/Button";
import { sendSmsFactor, submitMfaForBoth, validateToken } from "./ForgotPasswordAxiosFuncs";
import { PageInfo } from "Components/PageInformation/PageInformation";
import { PATHS } from "Utils/PagePaths";
import { ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { maskEMail } from "Utils/utilityFunctions";

const ForgotPasswordEmailMFA = () => {

  const { token } = useParams();
  const loginState = store.getState().loginInfo;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isForward = location.state?.isForward;

  const [isLoading, setIsLoading] = useState(true);
  const [emailMfaToken, setEmailMfaToken] = useState("");
  const [mfaFactors, setMfaFactors] = useState({
    mfaEmail: null
  })
  const [showSendCodeMsg, setShowSendCodeMsg] = useState(false);
  const [disableSendCode, setDisableSendCode] = useState(false);
  const [globalErrorList, setGlobalErrorList] = useState([]);
  const [disableMfaTokenField, setDisableMfaTokenField] = useState(true);
  const tokenInputRef = useRef();

  // handle send six digit mfa code
  const handleEmailSendCode = async () => {

    // clear error
    setGlobalErrorList([]);
    // disable the send code button to avoid double click renable it after 30s
    setDisableSendCode(true);

    //send the token via the email
    const res = await sendSmsFactor(token, { userFactor: "EMAIL" });

    //console.log("handleEmailSendCode()", res);

    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.data?.globalErrorMessageList);
      setDisableSendCode(false);
    } else {
      setTimeout(() => {
        setDisableSendCode(false);
      }, 30000);
      setShowSendCodeMsg(true);
      setDisableMfaTokenField(false);
      setTimeout(() => {
        tokenInputRef.current.focus();
      }, 500);
    }
  }

  const handleEmailMfaChange = (e) => {
    let value = e.target.value;
    const regex = /^[0-9\b]+$/;

    //console.log("test mfa code", regex.test(value));

    if (value === '' || regex.test(value)) {
      setEmailMfaToken(e.target.value);
    }
  }

  // api to verify the MFA token
  const verifyEmailToken = async () => {
    // clear error
    setGlobalErrorList([]);
    setShowSendCodeMsg(false);

    const res = await submitMfaForBoth(token, emailMfaToken, "EMAIL", true);
    //console.log("verifyEmailToken", res);
    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
    } else {
      // go to reset password page
      navigate("/token/" + token + "/3", { state: { isForward: true } });
    }
  }

  const cancelMfa = () => {
    navigate(PATHS.public['Sign In']);
  }

  // init load to validate the token in the URL from the email
  const initLoad = async (token) => {
    // the Forgot Password MFA can only be operated when the user is not login!
    if (loginState.token) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.GENERAL_ERROR.title,
          msg: "Please sign out to reset password.",
          showContactHelp: false
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        }
      });
      //stay on the  page
      setIsLoading(true);
      return;
    }

    // validate the token 
    const res = await validateToken(token)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });

    //console.log("initLoad() forgotpassword email mfa", res);
    if (res && res.data?.status !== 1) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: res.data.globalErrorMessageList,
          showContactHelp: true
        },
        redirect: {
          redirect: true,
          redirectFirst: true,
          path: PATHS.public['Get Help'],
          data: {}
        }
      });
      return;
    }
    if (res && res.data?.status === 1) {

      // from select mfa page, display the page content
      if (isForward) {
        setIsLoading(false);
        setMfaFactors({
          mfaEmail: res.data.data?.email
        });

        return;
      }
      // go back to the select mfa page and sort out the flow 
      navigate("/token/" + token);
    }
  }

  // init
  useEffect(() => {
    initLoad(token);
  }, []);


  return (
    <>
      {isLoading ? null :
        <Row className="mb-3">
          <Col xl={10} lg={11} md={11} sm={12}>
            <Card id="forgotpwd-email-mfa" className="p-4">
              <Card.Body className="text-start">
                <h1 id="mainContent" className="text-start mb-3 blue fw-bold">
                  Two-Factor Authentication
                </h1>
                <p>
                  To begin the verification process, please confirm your email address.
                </p>
                <hr></hr>
                {showSendCodeMsg ?
                  <PageInfo
                    type="info"
                    className
                    message={"A code has been sent to the email address associated with your account. Please enter that code in the field below. It may take up to 2 minutes to receive this code. If you do not see the email after 2 minutes, please check your spam folder."}></PageInfo>
                  : null}
                {globalErrorList.map((item, index) =>
                  <>
                    <Row className="pt-4"></Row>
                    <PageInfo key={"errors_" + index} type="error" message={item}></PageInfo>
                  </>
                )}
                <Form
                  className="pt-4"
                >
                  <Row className="mb-3 align-items-center">
                    <Col sm="5" className="mfa-setting-label pt-1">
                      <div class="mfa-setting-step">
                        <span>1.</span>
                        <span>Click to send code to <span className="email-label">{maskEMail(mfaFactors?.mfaEmail)}</span>.</span>
                      </div>
                    </Col>
                    <Col>
                      <TextButton
                        disabled={disableSendCode}
                        variant="normal"
                        onClick={handleEmailSendCode}
                        content={"Send Code"}
                      >
                      </TextButton>
                    </Col>
                  </Row >
                  <br />
                  <Form.Group as={Row} className="mb-3 align-items-center" controlId="mfaToken">
                    <Form.Label column sm="5" className="mfa-setting-label">
                      2. Enter the six digit code.
                    </Form.Label>
                    <Col sm={3}>
                      <Form.Control
                        //style={{ width: "7rem" }}
                        ref={tokenInputRef}
                        disabled={disableMfaTokenField}
                        type="text"
                        maxlength={6}
                        value={emailMfaToken}
                        onChange={handleEmailMfaChange}
                      />
                    </Col>
                  </Form.Group>
                  <br />
                  <Row className="mb-3 pt-1 align-items-center">
                    <Col sm="5" className="mfa-setting-label pt-1">3. Click Verify to confirm.</Col>
                    <Col>
                      <TextButton
                        className="w-10 me-4 mt-2"
                        variant="normal"
                        disabled={emailMfaToken.length !== 6}
                        onClick={verifyEmailToken}
                        content={"Verify"}
                      >
                      </TextButton>
                      <TextButton
                        className="w-10 mt-2"
                        variant="outline"
                        onClick={cancelMfa}
                        content={"Cancel"}
                      >
                      </TextButton>
                    </Col>
                  </Row>
                </Form >
              </Card.Body>
            </Card>
          </Col>
        </Row>}
    </>
  )
}

export default ForgotPasswordEmailMFA;